import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Internal tabs on the Shadow AI **Tools** page. Network Discovery is a separate
 * sidebar sub-page (its own route + DataTable, which owns its own state), not
 * one of these tabs.
 */
export type ShadowAiTabValue = 'browser' | 'installed' | 'running';

const VALID_TABS: readonly ShadowAiTabValue[] = [
  'browser',
  'installed',
  'running',
];

/**
 * Resolve the active Tools tab from the `?tab=` URL param. Legacy links used a
 * single `tab=tools` value (the old 3-table stack); map those to the first
 * per-table tab (`browser`). Unknown/absent values also fall back to `browser`.
 */
function parseTabParam(raw: string | null): ShadowAiTabValue {
  if (raw === 'tools') return 'browser';
  return VALID_TABS.includes(raw as ShadowAiTabValue)
    ? (raw as ShadowAiTabValue)
    : 'browser';
}

/** Drill-down `source` values that map 1:1 to a per-table tab. */
const SOURCE_TO_TAB: Record<
  'browser' | 'installed' | 'running',
  ShadowAiTabValue
> = {
  browser: 'browser',
  installed: 'installed',
  running: 'running',
};

export interface OrgShadowAiState {
  activeTab: ShadowAiTabValue;
  highlightToolId: string | null;
}

export interface OrgShadowAiActions {
  setActiveTab: (tab: ShadowAiTabValue) => void;
  handleUserClick: (
    userId: string,
    toolId: string,
    userInfo?: { email?: string; name?: string },
    source?: 'installed' | 'browser' | 'running',
    processId?: string,
    /**
     * Installed-tools rows are keyed by `${toolId}::${category}`, so the
     * drill-down needs the category to re-expand the originating row
     * (KIR-3495).
     */
    category?: string
  ) => void;
  handleRunningNowUserClick: (
    userId: string,
    toolId: string,
    processId: string,
    userInfo?: { email?: string; name?: string }
  ) => void;
  handleBackFromUser: () => void;
}

/**
 * Tools-page state: the active per-table tab (persisted in `?tab=`) and the user
 * drill-down navigation (`?user=` + `source`, with back-nav restoring the
 * originating tab).
 */
export function useOrgShadowAiState(): [OrgShadowAiState, OrgShadowAiActions] {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive the active tab from the URL (single source of truth) so browser
  // back/forward and any other history change stay in sync with the tabs,
  // rather than freezing at the value read once on mount.
  const activeTab = parseTabParam(searchParams.get('tab'));

  const setActiveTab = useCallback(
    (tab: ShadowAiTabValue) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', tab);
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const [highlightToolId, setHighlightToolId] = useState<string | null>(
    searchParams.get('highlightTool')
  );
  useEffect(() => {
    if (highlightToolId) {
      const timer = setTimeout(() => setHighlightToolId(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [highlightToolId]);

  const handleUserClick = useCallback(
    (
      userId: string,
      toolId: string,
      userInfo?: { email?: string; name?: string },
      source?: 'installed' | 'browser' | 'running',
      processId?: string,
      category?: string
    ) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('user', userId);
        next.set('tool', toolId);
        if (userInfo?.name) next.set('name', userInfo.name);
        if (userInfo?.email) next.set('email', userInfo.email);
        if (source) next.set('source', source);
        if (processId) next.set('process', processId);
        if (category) next.set('category', category);
        return next;
      });
    },
    [setSearchParams]
  );

  const handleRunningNowUserClick = useCallback(
    (
      userId: string,
      toolId: string,
      processId: string,
      userInfo?: { email?: string; name?: string }
    ) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('user', userId);
        next.set('tool', toolId);
        if (userInfo?.name) next.set('name', userInfo.name);
        if (userInfo?.email) next.set('email', userInfo.email);
        next.set('source', 'running');
        next.set('process', processId);
        return next;
      });
    },
    [setSearchParams]
  );

  const handleBackFromUser = useCallback(() => {
    const toolId = searchParams.get('tool');
    const userId = searchParams.get('user');
    const source = searchParams.get('source');

    if (
      source === 'running' ||
      source === 'browser' ||
      source === 'installed'
    ) {
      // Return to the per-table tab the drill-down originated from so back-nav
      // lands on the right table (KIR-3903), then strip the nav params. The
      // tab is derived from the `tab` param below, so no separate state set.
      const targetTab = SOURCE_TO_TAB[source];
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('user');
        next.delete('tool');
        next.delete('source');
        next.delete('name');
        next.delete('email');
        next.delete('process');
        next.delete('category');
        next.set('tab', targetTab);
        return next;
      });
    } else if (toolId) {
      setSearchParams({
        tool: toolId,
        highlight: userId ?? '',
        highlightTool: toolId,
      });
      setHighlightToolId(toolId);
    } else {
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const state: OrgShadowAiState = {
    activeTab,
    highlightToolId,
  };

  const actions: OrgShadowAiActions = {
    setActiveTab,
    handleUserClick,
    handleRunningNowUserClick,
    handleBackFromUser,
  };

  return [state, actions];
}
